import { getLogLevel, logDebug, LogLevel, type Unsubscribe } from '@amazon/vinyl-util'
import { setClassToken } from '@amazon/vinyl-tsx'
import { data } from '@amazon/vinyl-observable'
import { generateTheme } from '@amzn/bf-image-details'
import { ctx } from '@/util/context'
import { getRouter } from '@/router/router'
import { globalData } from '@amzn/bf-playback'

const { target } = ctx('DynamicThemeColors')

/**
 * App-wide image-derived theme. `bindThemeColors` stamps the `bg` seed (the
 * derived `--bg-g1…` wash) and the `accent` seed (the derived `--accent-color-*`
 * / `--secondary-accent-color-*` pairs) on the document root; a null seed clears
 * its vars so `styles.scss` falls back to the default wash / grayscale accent.
 */
export type ThemeColors = {
    readonly bg: string | null
    readonly accent: string | null
}

export const themeColors$ = globalData(() => data<ThemeColors>({ bg: null, accent: null }))

/**
 * The teal brand seed. The global no-image fallback is grayscale (styles.scss);
 * an imageless page that wants the teal brand theme opts in explicitly with
 * `setThemeColors({ …, accent: TEAL_SEED })`, deriving the same OKLCH accent +
 * secondary every image page gets.
 */
export const TEAL_SEED = '#25d1da'

/** Bauhaus `darkBlue`, the bg seed of the default theme (a dark blue wash in dark theme, pale blue in light). */
export const DARK_BLUE_SEED = '#000a8e'

export const DEFAULT_THEME_COLORS: ThemeColors = { bg: DARK_BLUE_SEED, accent: TEAL_SEED }

const pageClaimedTheme$ = globalData(() => data(false))

/** Mark the page being shown as owning the theme, so navigation doesn't reset it to the default. */
export function claimThemeColors() {
    pageClaimedTheme$.value = true
}

export function setThemeColors(colors: ThemeColors) {
    claimThemeColors()
    themeColors$.value = colors
}

export function setDefaultThemeColors() {
    setThemeColors(DEFAULT_THEME_COLORS)
}

export function setDefaultThemeColorsIfUnset() {
    claimThemeColors()
    const { bg, accent } = themeColors$.value
    if (bg == null && accent == null) setDefaultThemeColors()
}

/** Reset the app theme to the stylesheet wash and grayscale accent. */
export function clearThemeColors() {
    themeColors$.value = { bg: null, accent: null }
}

/**
 * Reset the theme to the stylesheet default when a shown page never set its own,
 * so the previous page's colors (a t1r hero's artwork) don't outlive it. A page
 * that claims the theme (sets it, or starts tracking an image still loading)
 * keeps the previous colors held until its own resolve, with no flash to gray.
 */
export function bindThemeReset(): Unsubscribe {
    const router = getRouter()
    const offNavigate = router.on('navigate', () => {
        pageClaimedTheme$.value = false
    })
    const offShown = router.on('pageShown', () => {
        if (!pageClaimedTheme$.value) clearThemeColors()
    })
    return () => {
        offNavigate()
        offShown()
    }
}

const THEME_TRANSITION_CLASS = 'themeColorsTransition'

/**
 * Subscribe `themeColors$` and stamp the image-derived theme on the DOM until
 * the returned `Unsubscribe` runs. After the first theme lands, later themes
 * blend (`themeColors.scss` transitions the registered color vars). Both the
 * bg and accent vars go on `document.documentElement` — where `data-bb-theme` lives, so each resolves
 * against the theme selector and inherits down to the body-level `--bg-g1` /
 * `--chrome-bg` / chat-surface consumers. The radial-paint class toggles on
 * `document.body`, the element that paints the page-background gradient.
 */
export function bindThemeColors(): Unsubscribe {
    const root = document.documentElement
    const unsubscribe = themeColors$.onData(({ bg, accent }) => {
        applyBgColors(root, bg)
        setClassToken(document.body, 'backgroundRadial', bg != null)
        applyAccentColors(root, accent)
        if (bg != null || accent != null) armThemeTransition(root)
        // ?logLevel=debug — seeds + the derived theme they resolved to, or null on clear.
        if (getLogLevel() <= LogLevel.DEBUG) {
            logDebug(target, 'theme set', {
                bgSeed: bg,
                accentSeed: accent,
                bgTheme: bg ? generateTheme(bg) : null,
                accentTheme: accent ? generateTheme(accent) : null,
            })
        }
    })
    return () => {
        unsubscribe()
        setClassToken(root, THEME_TRANSITION_CLASS, false)
    }
}

function armThemeTransition(root: HTMLElement) {
    if (root.classList.contains(THEME_TRANSITION_CLASS)) return
    // Flush the first theme's style before arming, so it paints instantly and only later themes blend.
    getComputedStyle(root).getPropertyValue('--accent-color-dark')
    root.classList.add(THEME_TRANSITION_CLASS)
}

function setVar(target: HTMLElement, prop: string, value: string | undefined) {
    if (value) target.style.setProperty(prop, value)
    else target.style.removeProperty(prop)
}

/**
 * Stamp the derived page-background wash from a seed color, or clear every
 * `--bg-g{n}-{dark,light}` var on a null seed so `styles.scss` falls back to the
 * default wash. All three slots share the single derived bg per theme.
 */
export function applyBgColors(target: HTMLElement, seed: string | null) {
    const pair = seed ? generateTheme(seed) : null
    for (let i = 1; i <= 3; i++) {
        setVar(target, `--bg-g${i}-dark`, pair?.dark.bg)
        setVar(target, `--bg-g${i}-light`, pair?.light.bg)
    }
}

/**
 * Stamp the derived primary + secondary accent pairs from a seed color, or clear
 * all four vars on a null seed so the stylesheet's grayscale default accent
 * fires. `styles.scss` resolves `--accent-color` / `--secondary-accent-color`
 * from the theme-correct half; consumers use those contrast-nudged pairs directly.
 */
export function applyAccentColors(target: HTMLElement, seed: string | null) {
    const pair = seed ? generateTheme(seed) : null
    setVar(target, '--accent-color-dark', pair?.dark.accent)
    setVar(target, '--accent-color-light', pair?.light.accent)
    setVar(target, '--secondary-accent-color-dark', pair?.dark.secondary)
    setVar(target, '--secondary-accent-color-light', pair?.light.secondary)
}
